import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditAttachment } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardAttachment } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import { assign, find, get, map } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStackId: string
  sectionId: string
  source: string
  originalAttachments: CardAttachment[]
  files: Array<CardAttachment>
}

export const LESSON_CARD_ADD_ATTACHMENTS = {
  name: "LESSON_CARD_ADD_ATTACHMENTS",

  patches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    }).push("embedded", "attributes.attachments", ["$each", payload.files]).patch

    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    })

    patch.set("embedded", "attributes.attachments", payload.originalAttachments)

    return [patch.patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let titleForAnalytics = "Attachment - Other"
    let fileCount = get(payload, "files.length")
    let fileCountInWords = fileCount === 1 ? "1 File" : `${fileCount} files`
    let card = find(models.cardStack!.attributes.cards, {
      id: payload.sectionId,
    })

    if (card === undefined) throw new Error("No Card passed in card stack to comment on ")

    let cardTitle = "title" in card.attributes ? card.attributes.title : null

    let attachmentTitles = map(payload.files, (file) => {
      return file.title
    })
    let context = defaultLessonContext(payload, models)
    let newContext = assign({}, context, {
      cardTitle: cardTitle,
      fileCount: fileCount,
      attachmentTitles,
    })
    let attachmentId = get(payload.originalAttachments, "id")
    let newEvent: LessonEditAttachment = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "attachments",

      adverbial_verb: "adding",
      adverbial_object_type: "attachment",
      adverbial_object_specifier: "other",
      adverbial_object_id: attachmentId!,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    if (payload.source === "drag") {
      titleForAnalytics = "Attachment - Upload File"
      newEvent.adverbial_object_specifier = "upload_file"
    } else if (payload.source === "dropbox") {
      titleForAnalytics = "Attachment - Add from Dropbox"
      newEvent.adverbial_object_specifier = "dropbox_file"
    } else if (payload.source === "google") {
      titleForAnalytics = "Attachment - Add from Google Drive"
      newEvent.adverbial_object_specifier = "google_drive_file"
    } else if (payload.source === "onedrive") {
      titleForAnalytics = "Attachment - Add from OneDrive"
      newEvent.adverbial_object_specifier = "one_drive_file"
    } else {
      null
    }

    return {
      title: "Attached a File",
      titleForAnalytics: titleForAnalytics,
      description: `Added ${fileCountInWords} to "${cardTitle}" card`,
      context: newContext,
      activity: defaultLessonActivity(payload, models, "created"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_ADD_ATTACHMENTS
